// Výživa / Databáze jídelníčku (22.8.2026, fáze 2 přestavby generátoru).
//
// Centrální databáze receptů a textů se schvalováním nutriční specialistkou.
// Owner navíc vidí záložku Přehled a log: statistiku schvalování a historii
// všech úprav. Data drží PG (jidelnicek_recepty_v2 + jidelnicek_texty),
// endpoint /api/vyziva-databaze.
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Zalozky, RozbalovaciVyber, Hlaska, SkeletonRows, EmptyState } from "../../components/ui.jsx";
import { toast } from "../../components/Toast.jsx";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PoznamkaPole from "../../components/PoznamkaPole.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { Database, Check, X, Pencil, Loader2, Search, RotateCcw, ChevronDown, ChevronRight, Plus, Trash2, CheckSquare, Square, Download, Archive } from "lucide-react";

const STAV_META = {
  ceka: { label: "Čeká", pill: "bg-amber-50 text-amber-800 border-amber-200", tecka: "bg-amber-400" },
  schvaleno: { label: "Schváleno", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", tecka: "bg-emerald-500" },
  zamitnuto: { label: "Zamítnuto", pill: "bg-slate-100 text-slate-500 border-slate-200", tecka: "bg-slate-300" },
  archiv: { label: "Archiv", pill: "bg-slate-100 text-slate-500 border-slate-200", tecka: "bg-slate-400" },
};
const CHOD_LABEL = { snidane: "Snídaně", svacina: "Svačina", obed: "Oběd", vecere: "Večeře" };
const TYP_LABEL = { uvod: "Úvod", tip: "Tip", kapitola: "Kapitola", jine: "Jiné" };
const AKCE_LABEL = {
  "recept-schvalit": "schválil(a) recept", "recept-zamitnout": "zamítl(a) recept",
  "recept-vratit": "vrátil(a) recept ke kontrole", "recept-upravit": "upravil(a) recept",
  "text-pridat": "přidal(a) text", "text-upravit": "upravil(a) text",
  "text-schvalit": "schválil(a) text", "text-zamitnout": "zamítl(a) text",
  "text-archivovat": "archivoval(a) text", "text-obnovit": "obnovil(a) text z archivu",
  "text-smazat": "smazal(a) text", seed: "naplnil(a) databázi",
};

const fmtCas = (d) => (d ? new Date(d).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

async function post(body) {
  const r = await apiFetch("/api/vyziva-databaze", { method: "POST", body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Akce selhala");
  return j;
}

// ── Editor jedné varianty receptu ───────────────────────────────────────────
function VariantaEditor({ varianta, index, onChange }) {
  const set = (k, v) => onChange(index, { ...varianta, [k]: v });
  const num = (k) => (e) => set(k, e.target.value.replace(/[^0-9]/g, ""));
  const kcal = 4 * (Number(varianta.b) || 0) + 4 * (Number(varianta.s) || 0) + 9 * (Number(varianta.t) || 0);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-bold text-slate-700">Varianta {index + 1}</span>
        <span className="rounded-full bg-slate-200 px-2 py-0.5 font-semibold tabular-nums">{kcal} kcal (z maker)</span>
        {["b", "s", "t"].map((k) => (
          <label key={k} className="inline-flex items-center gap-1">
            <span className="font-semibold uppercase text-slate-500">{k}</span>
            <input value={varianta[k] ?? ""} onChange={num(k)} inputMode="numeric"
              className="field w-14 tabular-nums" />
            <span className="text-slate-400">g</span>
          </label>
        ))}
      </div>
      <label className="block">
        <span className="text-xs font-semibold text-slate-600">Suroviny (jedna na řádek)</span>
        <textarea rows={Math.min(8, Math.max(3, (varianta.suroviny || []).length))} className="field mt-1 font-mono text-xs"
          value={Array.isArray(varianta.suroviny) ? varianta.suroviny.join("\n") : varianta.suroviny || ""}
          onChange={(e) => set("suroviny", e.target.value.split("\n"))} />
      </label>
      <label className="block">
        <span className="text-xs font-semibold text-slate-600">Postup</span>
        <textarea rows={3} className="field mt-1" value={varianta.postup || ""} onChange={(e) => set("postup", e.target.value)} />
      </label>
    </div>
  );
}

// ── Karta receptu ───────────────────────────────────────────────────────────
function ReceptKarta({ meta, vybrano, onVybrat, onChanged, smiSchvalovat }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const stavMeta = STAV_META[meta.stav] || STAV_META.ceka;

  const otevri = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (!detail) {
      try {
        const r = await apiFetch(`/api/vyziva-databaze?recept=${encodeURIComponent(meta.id)}`);
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Načtení selhalo");
        setDetail(j.recept);
      } catch (e) { toast(e.message, "error"); setOpen(false); }
    }
  };

  const zacniEdit = () => {
    setForm({
      nazev: detail.data.nazev || "",
      chod: detail.data.chod || meta.chod,
      varianty: (detail.data.varianty || []).map((v) => ({ ...v })),
    });
    setEdit(true);
  };

  const ulozit = async () => {
    setBusy("ulozit");
    try {
      const j = await post({ action: "recept-upravit", id: meta.id, ...form });
      setDetail((d) => ({ ...d, data: j.recept }));
      setEdit(false);
      toast("Recept uložen.", "success");
      onChanged({ id: meta.id, nazev: j.recept.nazev, chod: j.recept.chod });
    } catch (e) { toast(e.message, "error"); }
    finally { setBusy(""); }
  };

  const akce = async (action, okMsg) => {
    setBusy(action);
    try {
      const j = await post({ action, id: meta.id });
      toast(okMsg, "success");
      onChanged({ id: meta.id, stav: j.stav });
    } catch (e) { toast(e.message, "error"); }
    finally { setBusy(""); }
  };

  return (
    <div className={`rounded-2xl border bg-white shadow-sm transition ${vybrano ? "border-brand-400 ring-1 ring-brand-200" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 p-3.5">
        {smiSchvalovat && meta.stav === "ceka" && (
          <button type="button" onClick={() => onVybrat(meta.id)} className="shrink-0 text-slate-400 hover:text-brand-600" title="Vybrat pro hromadné schválení">
            {vybrano ? <CheckSquare className="w-5 h-5 text-brand-600" /> : <Square className="w-5 h-5" />}
          </button>
        )}
        <button type="button" onClick={otevri} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${stavMeta.tecka}`} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold text-slate-900">{meta.nazev}</span>
            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
              <span>{CHOD_LABEL[meta.chod] || meta.chod}</span>
              <span aria-hidden className="h-3 w-px bg-slate-300" />
              <span>{meta.variant} {meta.variant === 1 ? "varianta" : meta.variant <= 4 ? "varianty" : "variant"}</span>
              {Array.isArray(meta.kcaly) && meta.kcaly.length > 0 && (
                <>
                  <span aria-hidden className="h-3 w-px bg-slate-300" />
                  <span className="tabular-nums">{meta.kcaly.filter(Boolean).join(" / ")} kcal</span>
                </>
              )}
              {meta.resil && (
                <>
                  <span aria-hidden className="h-3 w-px bg-slate-300" />
                  <span>{meta.resil} {fmtCas(meta.resil_at)}</span>
                </>
              )}
            </span>
          </span>
          <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${stavMeta.pill}`}>{stavMeta.label}</span>
          {open ? <ChevronDown className="w-4 h-4 shrink-0 text-slate-400" /> : <ChevronRight className="w-4 h-4 shrink-0 text-slate-400" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-100 p-3.5 pt-3">
          {!detail && <div className="flex items-center gap-2 py-3 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Načítám recept…</div>}

          {detail && !edit && (
            <div className="space-y-3">
              {(detail.data.varianty || []).map((v, i) => (
                <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                  <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-xs tabular-nums text-slate-600">
                    <span className="rounded-full bg-slate-200 px-2 py-0.5 font-semibold">{v.kcal} kcal</span>
                    <span className="rounded-full bg-white px-2 py-0.5 border border-slate-200">B {v.b} g</span>
                    <span className="rounded-full bg-white px-2 py-0.5 border border-slate-200">S {v.s} g</span>
                    <span className="rounded-full bg-white px-2 py-0.5 border border-slate-200">T {v.t} g</span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="text-sm text-slate-700">
                      {(v.suroviny || []).map((s, si) => <div key={si}>{s}</div>)}
                    </div>
                    <div className="text-sm text-slate-600">{v.postup}</div>
                  </div>
                </div>
              ))}
              {smiSchvalovat && (
                <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  <button type="button" onClick={zacniEdit}
                    className="btn btn-secondary">
                    <Pencil className="w-3.5 h-3.5" /> Upravit
                  </button>
                  {meta.stav !== "schvaleno" && (
                    <button type="button" disabled={!!busy} onClick={() => akce("recept-schvalit", "Recept schválen.")}
                      className="btn btn-primary">
                      {busy === "recept-schvalit" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Schválit
                    </button>
                  )}
                  {meta.stav !== "zamitnuto" && (
                    <button type="button" disabled={!!busy} onClick={() => akce("recept-zamitnout", "Recept zamítnut.")}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50">
                      {busy === "recept-zamitnout" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Zamítnout
                    </button>
                  )}
                  {meta.stav !== "ceka" && (
                    <button type="button" disabled={!!busy} onClick={() => akce("recept-vratit", "Recept vrácen ke kontrole.")}
                      className="btn btn-secondary">
                      {busy === "recept-vratit" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Vrátit ke kontrole
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {detail && edit && form && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Název</span>
                  <input className="field mt-1" value={form.nazev} onChange={(e) => setForm((f) => ({ ...f, nazev: e.target.value }))} />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Chod</span>
                  <select className="field mt-1 bg-white" value={form.chod} onChange={(e) => setForm((f) => ({ ...f, chod: e.target.value }))}>
                    {Object.entries(CHOD_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
              </div>
              {form.varianty.map((v, i) => (
                <VariantaEditor key={i} varianta={v} index={i}
                  onChange={(idx, nv) => setForm((f) => ({ ...f, varianty: f.varianty.map((x, xi) => (xi === idx ? nv : x)) }))} />
              ))}
              <p className="text-xs text-slate-500">Kcal se ukládá vždy dopočtené z maker (4×B + 4×S + 9×T), uložení projde deterministickým čističem textu.</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEdit(false)} disabled={busy === "ulozit"}
                  className="btn btn-secondary">
                  Zrušit
                </button>
                <button type="button" onClick={ulozit} disabled={busy === "ulozit"}
                  className="btn btn-primary">
                  {busy === "ulozit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Uložit
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Karta textu ─────────────────────────────────────────────────────────────
// Zdrojové dokumenty (původní texty z historických jídelníčků) se neschvalují
// ani nepoužívají v jídelníčcích — slouží jen k porovnání s vlastním zněním.
const jeZdrojovy = (t) => /\(zdrojový dokument\)\s*$/i.test(String(t.nazev || ""));

function TextKarta({ text, onChanged, smiSchvalovat, jeOwner }) {
  const [open, setOpen] = useState(false);
  const [obsah, setObsah] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [potvrditSmazani, setPotvrditSmazani] = useState(false);
  const zdrojovy = jeZdrojovy(text);
  const meta = zdrojovy
    ? { label: "Jen pro porovnání", pill: "bg-slate-100 text-slate-500 border-slate-200", tecka: "bg-slate-300" }
    : (STAV_META[text.stav] || STAV_META.ceka);

  const otevri = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (obsah == null) {
      try {
        const j = await post({ action: "text-obsah", id: text.id });
        setObsah(j.text.obsah);
      } catch (e) { toast(e.message, "error"); setOpen(false); }
    }
  };

  const akce = async (action, okMsg) => {
    setBusy(action);
    try {
      await post({ action, id: text.id });
      toast(okMsg, "success");
      onChanged();
    } catch (e) { toast(e.message, "error"); }
    finally { setBusy(""); }
  };

  const ulozit = async () => {
    setBusy("ulozit");
    try {
      await post({ action: "text-upravit", id: text.id, ...form });
      setObsah(form.obsah);
      setEdit(false);
      toast("Text uložen.", "success");
      onChanged();
    } catch (e) { toast(e.message, "error"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm">
      <button type="button" onClick={otevri} className="flex w-full items-center gap-3 text-left">
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${meta.tecka}`} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-slate-900">{text.nazev}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
            <span>{TYP_LABEL[text.typ] || text.typ}</span>
            <span aria-hidden className="h-3 w-px bg-slate-300" />
            <span className="tabular-nums">{Math.round((text.znaku || 0) / 100) / 10} tis. znaků</span>
            {text.resil && (
              <>
                <span aria-hidden className="h-3 w-px bg-slate-300" />
                <span>{text.resil} {fmtCas(text.resil_at)}</span>
              </>
            )}
          </span>
        </span>
        <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.pill}`}>{meta.label}</span>
        {open ? <ChevronDown className="w-4 h-4 shrink-0 text-slate-400" /> : <ChevronRight className="w-4 h-4 shrink-0 text-slate-400" />}
      </button>

      {open && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          {obsah == null && <div className="flex items-center gap-2 py-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>}
          {obsah != null && !edit && (
            <div className="space-y-3">
              <article className="prose prose-slate prose-sm max-w-none max-h-96 overflow-y-auto rounded-xl bg-slate-50 p-4
                prose-headings:font-bold prose-headings:text-slate-900
                prose-h1:text-lg prose-h2:text-base prose-h3:text-sm prose-h3:uppercase prose-h3:tracking-wide">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{obsah}</ReactMarkdown>
              </article>
              {zdrojovy && (
                <div className="flex flex-wrap items-center gap-2">
                  <p className="min-w-0 flex-1 text-xs text-slate-500">
                    Původní text z historických jídelníčků. Neschvaluje se a do jídelníčků se nepoužívá,
                    slouží jen k porovnání s vlastním zněním.
                  </p>
                  {jeOwner && (
                    <button type="button" disabled={!!busy} onClick={() => setPotvrditSmazani(true)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                      <Trash2 className="w-3.5 h-3.5" /> Smazat
                    </button>
                  )}
                </div>
              )}
              {smiSchvalovat && !zdrojovy && (
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => { setForm({ typ: text.typ, nazev: text.nazev, obsah }); setEdit(true); }}
                    className="btn btn-secondary">
                    <Pencil className="w-3.5 h-3.5" /> Upravit
                  </button>
                  {text.stav !== "schvaleno" && (
                    <button type="button" disabled={!!busy} onClick={() => akce("text-schvalit", "Text schválen.")}
                      className="btn btn-primary">
                      <Check className="w-3.5 h-3.5" /> Schválit
                    </button>
                  )}
                  {text.stav !== "zamitnuto" && text.stav !== "archiv" && (
                    <button type="button" disabled={!!busy} onClick={() => akce("text-zamitnout", "Text zamítnut.")}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50">
                      <X className="w-3.5 h-3.5" /> Zamítnout
                    </button>
                  )}
                  {text.stav !== "archiv" ? (
                    <button type="button" disabled={!!busy} onClick={() => akce("text-archivovat", "Text archivován, nebude se používat.")}
                      className="btn btn-secondary">
                      <Archive className="w-3.5 h-3.5" /> Archivovat
                    </button>
                  ) : (
                    <button type="button" disabled={!!busy} onClick={() => akce("text-obnovit", "Text obnoven z archivu.")}
                      className="btn btn-secondary">
                      <RotateCcw className="w-3.5 h-3.5" /> Obnovit z archivu
                    </button>
                  )}
                  {jeOwner && (
                    <button type="button" disabled={!!busy} onClick={() => setPotvrditSmazani(true)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                      <Trash2 className="w-3.5 h-3.5" /> Smazat
                    </button>
                  )}
                </div>
              )}
              <ConfirmDialog
                open={potvrditSmazani}
                title="Smazat text?"
                description={`Text „${text.nazev}" se nevratně smaže. Pokud ho jen nechcete používat, zvolte raději archivaci. Texty ze šablon v repu se po smazání při dalším načtení stránky obnoví.`}
                confirmLabel="Smazat"
                variant="danger"
                onConfirm={() => { setPotvrditSmazani(false); akce("text-smazat", "Text smazán."); }}
                onCancel={() => setPotvrditSmazani(false)}
              />
            </div>
          )}
          {edit && form && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Název</span>
                  <input className="field mt-1" value={form.nazev} onChange={(e) => setForm((f) => ({ ...f, nazev: e.target.value }))} />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Typ</span>
                  <select className="field mt-1 bg-white" value={form.typ} onChange={(e) => setForm((f) => ({ ...f, typ: e.target.value }))}>
                    {Object.entries(TYP_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
              </div>
              <PoznamkaPole value={form.obsah} onChange={(obsah) => setForm((f) => ({ ...f, obsah }))} minHeight="16rem" autoFocus />
              <div className="flex gap-2">
                <button type="button" onClick={() => setEdit(false)} className="btn btn-secondary">Zrušit</button>
                <button type="button" onClick={ulozit} disabled={busy === "ulozit"}
                  className="btn btn-primary">
                  {busy === "ulozit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Uložit
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Přehled a log (owner) ───────────────────────────────────────────────────
function PrehledLog() {
  const { data, loading } = useApiData("/api/vyziva-databaze?log=1");
  if (loading && !data) return <div className="flex items-center gap-2 py-6 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Načítám statistiku…</div>;
  const stat = data?.stat || {};
  const log = Array.isArray(data?.log) ? data.log : [];
  const pct = stat.celkem ? Math.round((stat.schvaleno / stat.celkem) * 100) : 0;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Celkem receptů", stat.celkem, "text-slate-900"],
          ["Schváleno", stat.schvaleno, "text-emerald-600"],
          ["Čeká", stat.ceka, "text-amber-600"],
          ["Zamítnuto", stat.zamitnuto, "text-slate-500"],
        ].map(([label, n, cls]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
            <div className={`mt-1 text-2xl font-bold tabular-nums ${cls}`}>{n ?? 0}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-semibold text-slate-700">Průběh schvalování databáze</span>
          <span className="tabular-nums font-bold text-emerald-600">{pct} %</span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span>Dnes schváleno: <b className="tabular-nums">{stat.schvalenoDnes ?? 0}</b></span>
          <span>Za 7 dní: <b className="tabular-nums">{stat.schvaleno7d ?? 0}</b></span>
        </div>
      </div>
      {Array.isArray(stat.poAutorech) && stat.poAutorech.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-2 text-sm font-semibold text-slate-700">Úpravy a schválení podle lidí</div>
          <div className="space-y-1.5">
            {stat.poAutorech.map((a) => (
              <div key={a.autor} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{a.autor}</span>
                <span className="tabular-nums font-semibold text-slate-900">{a.n}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4 text-sm font-semibold text-slate-700">Log posledních změn</div>
        <div className="max-h-[480px] divide-y divide-slate-50 overflow-y-auto">
          {log.map((l, i) => (
            <div key={i} className="flex items-baseline gap-2 px-4 py-2 text-sm">
              <span className="shrink-0 tabular-nums text-xs text-slate-400">{fmtCas(l.ts)}</span>
              <span className="min-w-0 flex-1 text-slate-700">
                <b>{l.autor}</b> {AKCE_LABEL[l.akce] || l.akce}{l.nazev ? <> „{l.nazev}"</> : null}
              </span>
            </div>
          ))}
          {!log.length && <div className="px-4 py-6 text-center text-sm text-slate-400">Zatím žádné změny.</div>}
        </div>
      </div>
    </div>
  );
}

// ── Hlavní stránka ──────────────────────────────────────────────────────────
export default function VyzivaDatabaze() {
  const { data, loading, refetch } = useApiData("/api/vyziva-databaze?prehled=1");
  const [tab, setTab] = useState("recepty"); // recepty | texty | prehled
  const [filtr, setFiltr] = useState("vse");
  const [chod, setChod] = useState("vse");
  const [hledej, setHledej] = useState("");
  const [limit, setLimit] = useState(100);
  const [vybrane, setVybrane] = useState(() => new Set());
  const [busy, setBusy] = useState("");
  const [novyText, setNovyText] = useState(false);
  const [novyForm, setNovyForm] = useState({ typ: "uvod", nazev: "", obsah: "" });
  const [zobrazZdrojove, setZobrazZdrojove] = useState(false);
  // Lokální patch metadat (po akci na kartě nemusíme přenačítat 1700 řádků).
  const [patch, setPatch] = useState({});
  const seedBezi = useRef(false);

  const jeOwner = !!data?.jeOwner;
  const smiSchvalovat = !!data?.smiSchvalovat;

  const recepty = useMemo(() => {
    const vse = (Array.isArray(data?.recepty) ? data.recepty : []).map((r) => (patch[r.id] ? { ...r, ...patch[r.id] } : r));
    const q = hledej.trim().toLowerCase();
    return vse
      .filter((r) => filtr === "vse" || r.stav === filtr)
      .filter((r) => chod === "vse" || r.chod === chod)
      .filter((r) => !q || String(r.nazev || "").toLowerCase().includes(q));
  }, [data, filtr, chod, hledej, patch]);

  // Počty stavů se přepočítávají podle zvoleného chodu a hledání — po výběru
  // chodu jsou čísla menší a schvalování vypadá zvládnutelněji (22.8.2026).
  const pocty = useMemo(() => {
    const q = hledej.trim().toLowerCase();
    const vse = (Array.isArray(data?.recepty) ? data.recepty : [])
      .map((r) => (patch[r.id] ? { ...r, ...patch[r.id] } : r))
      .filter((r) => chod === "vse" || r.chod === chod)
      .filter((r) => !q || String(r.nazev || "").toLowerCase().includes(q));
    const p = { vse: vse.length, ceka: 0, schvaleno: 0, zamitnuto: 0 };
    for (const r of vse) if (p[r.stav] != null) p[r.stav]++;
    return p;
  }, [data, patch, chod, hledej]);

  useEffect(() => { setLimit(100); }, [filtr, chod, hledej]);

  const onChanged = (zmena) => {
    setPatch((p) => ({ ...p, [zmena.id]: { ...(p[zmena.id] || {}), ...zmena } }));
    if (zmena.stav) setVybrane((s) => { const n = new Set(s); n.delete(zmena.id); return n; });
  };

  const vybrat = (id) => setVybrane((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const schvalitVybrane = async () => {
    const ids = [...vybrane].slice(0, 100);
    if (!ids.length) return;
    setBusy("hromadne");
    try {
      const j = await post({ action: "schvalit-hromadne", ids });
      toast(`Schváleno ${j.schvaleno} receptů.`, "success");
      setPatch((p) => { const n = { ...p }; for (const id of ids) n[id] = { ...(n[id] || {}), stav: "schvaleno" }; return n; });
      setVybrane(new Set());
    } catch (e) { toast(e.message, "error"); }
    finally { setBusy(""); }
  };

  // Naplnění databáze: opakované volání seed, dokud zbyva > 0.
  const naplnit = async () => {
    if (seedBezi.current) return;
    seedBezi.current = true;
    setBusy("seed");
    try {
      let zbyva = 1, kol = 0;
      while (zbyva > 0 && kol < 12) {
        const j = await post({ action: "seed" });
        zbyva = j.zbyva || 0;
        kol++;
      }
      toast("Databáze naplněna.", "success");
      refetch();
    } catch (e) { toast(e.message, "error"); }
    finally { setBusy(""); seedBezi.current = false; }
  };

  const pridatText = async () => {
    if (!novyForm.nazev.trim() || !novyForm.obsah.trim()) { toast("Vyplňte název i obsah", "error"); return; }
    setBusy("novy-text");
    try {
      await post({ action: "text-pridat", ...novyForm });
      toast("Text přidán.", "success");
      setNovyText(false);
      setNovyForm({ typ: "uvod", nazev: "", obsah: "" });
      refetch();
    } catch (e) { toast(e.message, "error"); }
    finally { setBusy(""); }
  };

  const texty = Array.isArray(data?.texty) ? data.texty : [];

  return (
    <Stranka sirka="uzka">
      <GradientHeader
        icon={Database}
        title="Databáze jídelníčku"
        napoveda="Recepty a texty, ze kterých generátor staví jídelníčky. Nový recept musí schválit nutriční specialistka, teprve pak ho generátor použije."
        loading={loading}
      />

      {smiSchvalovat && (data?.zbyvaSeed || 0) > 0 && (
        <Hlaska ton="pozor" title={`V databázi zbývá naplnit ${data.zbyvaSeed} receptů`}
          akce={
            <button type="button" disabled={busy === "seed"} onClick={naplnit} className="btn btn-primary shrink-0">
              {busy === "seed" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Naplnit
            </button>
          }>
          Recepty se doplní ze zdrojové databáze jídelníčků.
        </Hlaska>
      )}

      <Zalozky
        stitek="Část"
        aria-label="Část databáze"
        value={tab}
        onChange={setTab}
        items={[
          { key: "recepty", label: "Recepty" },
          { key: "texty", label: `Texty (${texty.length})` },
          ...(jeOwner ? [{ key: "prehled", label: "Přehled a log" }] : []),
        ]}
      />

      {tab === "recepty" && (
        <>
          <div className="space-y-2">
            <RozbalovaciVyber
              stitek="Stav"
              aria-label="Filtr podle stavu receptu"
              value={filtr}
              onChange={setFiltr}
              items={[
                { key: "vse", label: `Vše (${pocty.vse})` },
                { key: "ceka", label: `Čeká (${pocty.ceka})` },
                { key: "schvaleno", label: `Schváleno (${pocty.schvaleno})` },
                { key: "zamitnuto", label: `Zamítnuto (${pocty.zamitnuto})` },
              ]}
            />
            <RozbalovaciVyber
              stitek="Chod"
              aria-label="Filtr podle chodu"
              value={chod}
              onChange={setChod}
              items={[{ key: "vse", label: "Všechny chody" }, ...Object.entries(CHOD_LABEL).map(([k, l]) => ({ key: k, label: l }))]}
            />
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={hledej} onChange={(e) => setHledej(e.target.value)} placeholder="Hledat recept"
                className="field w-full pl-8 pr-3" />
            </div>
          </div>

          {smiSchvalovat && vybrane.size > 0 && (
            <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-slate-50/95 p-3 shadow-sm backdrop-blur">
              <span className="text-sm font-semibold text-slate-700">{vybrane.size} vybráno</span>
              <div className="flex flex-wrap items-center gap-1.5">
                <button type="button" disabled={busy === "hromadne"} onClick={schvalitVybrane} className="btn btn-primary">
                  {busy === "hromadne" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Schválit ({vybrane.size})
                </button>
                <button type="button" onClick={() => setVybrane(new Set())} className="btn btn-ghost">Zrušit výběr</button>
              </div>
            </div>
          )}

          {loading && !data && <SkeletonRows rows={6} />}

          {!loading && !recepty.length && (
            <EmptyState
              icon={Database}
              title={pocty.vse === 0 ? "Databáze je prázdná" : "Tomuhle filtru neodpovídá žádný recept"}
              hint={pocty.vse === 0 ? "Naplň ji tlačítkem v hlášce nahoře." : "Zkus jiný stav, jiný chod nebo vymaž hledání."}
            />
          )}

          <div className="space-y-2.5">
            {recepty.slice(0, limit).map((r) => (
              <ReceptKarta key={r.id} meta={r} vybrano={vybrane.has(r.id)} onVybrat={vybrat}
                onChanged={onChanged} smiSchvalovat={smiSchvalovat} />
            ))}
          </div>
          {recepty.length > limit && (
            <button type="button" onClick={() => setLimit((l) => l + 200)}
              className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Zobrazit dalších {Math.min(200, recepty.length - limit)} (celkem {recepty.length})
            </button>
          )}
        </>
      )}

      {tab === "texty" && (
        <div className="space-y-3">
          {smiSchvalovat && !novyText && (
            <button type="button" onClick={() => setNovyText(true)}
              className="btn btn-secondary">
              <Plus className="w-4 h-4" /> Přidat text
            </button>
          )}
          {novyText && (
            <div className="space-y-3 rounded-2xl border border-brand-200 bg-brand-50/50 p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Název</span>
                  <input className="field mt-1" value={novyForm.nazev} onChange={(e) => setNovyForm((f) => ({ ...f, nazev: e.target.value }))} />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Typ</span>
                  <select className="field mt-1 bg-white" value={novyForm.typ} onChange={(e) => setNovyForm((f) => ({ ...f, typ: e.target.value }))}>
                    {Object.entries(TYP_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
              </div>
              <PoznamkaPole value={novyForm.obsah} onChange={(obsah) => setNovyForm((f) => ({ ...f, obsah }))}
                placeholder="Text vlastními slovy, nesmí být totožný se zdrojovým jídelníčkem…" minHeight="12rem" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setNovyText(false)} className="btn btn-secondary">Zrušit</button>
                <button type="button" disabled={busy === "novy-text"} onClick={pridatText}
                  className="btn btn-primary">
                  {busy === "novy-text" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Přidat
                </button>
              </div>
            </div>
          )}
          {!texty.length && !novyText && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
              Zatím žádné texty. Zdrojové dokumenty se naplní spolu s recepty tlačítkem Naplnit databázi.
            </div>
          )}
          {texty.filter((t) => !jeZdrojovy(t)).map((t) => (
            <TextKarta key={t.id} text={t} onChanged={refetch} smiSchvalovat={smiSchvalovat} jeOwner={jeOwner} />
          ))}
          {texty.some(jeZdrojovy) && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3.5">
              <button type="button" onClick={() => setZobrazZdrojove((z) => !z)} className="flex w-full items-center gap-2 text-left">
                {zobrazZdrojove ? <ChevronDown className="w-4 h-4 shrink-0 text-slate-400" /> : <ChevronRight className="w-4 h-4 shrink-0 text-slate-400" />}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-slate-700">Zdrojové podklady ({texty.filter(jeZdrojovy).length})</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Původní texty z historických jídelníčků. Neschvalují se a do jídelníčků se nepoužívají,
                    jsou tu jen pro porovnání s vlastním zněním výše.
                  </span>
                </span>
              </button>
              {zobrazZdrojove && (
                <div className="mt-3 space-y-3">
                  {texty.filter(jeZdrojovy).map((t) => (
                    <TextKarta key={t.id} text={t} onChanged={refetch} smiSchvalovat={smiSchvalovat} jeOwner={jeOwner} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "prehled" && jeOwner && <PrehledLog />}
    </Stranka>
  );
}
